import { AxiosError } from "axios";

import { logger } from "@/utils/logger";

import { type LoginRequest, type accountInfo, type errorResponse, type LoginResponse } from "../interface/Auth";

import { axiosInstance } from "./axiosInstance";

function handleAxiosErrorForLogin(error: AxiosError): LoginResponse | null {
  let errorMessage = "An unknown error occurred.";
  if (error.response) {
    // Prioritize explicit rate limit message for 429
    if (error.response.status === 429) {
      errorMessage = "Too many failed attempts. Please try again later.";
    } else {
      const errorData = error.response.data as errorResponse;
      errorMessage = errorData.message || "An error occurred during login.";
    }
  } else if (error.request) {
    errorMessage = "Network error. Please check your connection.";
  } else {
    errorMessage = error.message;
  }
  return { token: "", message: errorMessage } as LoginResponse;
}

export async function loginFunction(
  email: string,
  password: string
): Promise<LoginResponse | null> {
  const loginData: LoginRequest = { email, password };
  try {
    const response = await axiosInstance.post("/api/v1/auth/localLogin", loginData);

    // Check if 2FA is required
    if (response.data?.requires2FA === true) {
      return {
        token: "",
        message: response.data.message || "Please enter your 2FA code",
        requires2FA: true,
      };
    }

    // Handle string response
    if (typeof response.data === "string") {
      const responseStr = response.data as string;
      if (responseStr.toLowerCase() === "login successful") {
        return {
          token: "success",
          message: responseStr,
        };
      }
      return { token: "", message: responseStr };
    }

    // Handle object response
    if (response.data && typeof response.data === "object") {
      const responseObj = response.data as {
        message?: string;
        token?: string;
        requires2FA?: boolean;
      };
      const message = responseObj.message;
      if (typeof message === "string" && message.toLowerCase() === "login successful") {
        return {
          token: responseObj.token ?? "success",
          message: message,
        };
      }
      return response.data as LoginResponse;
    }

    return response.data as LoginResponse;
  } catch (error) {
    logger.error("Login error", {
      error,
      context: "loginFunction",
      endpoint: "/api/v1/auth/localLogin",
    });
    if (error instanceof AxiosError) {
      const errorResponse = handleAxiosErrorForLogin(error);
      logger.debug("Processed login error response", {
        errorResponse,
        context: "loginFunction",
      });
      return errorResponse;
    }
    return { token: "", message: "An unexpected error occurred during login." } as LoginResponse;
  }
}

export const logout = async () => {
  const response = await axiosInstance.post("/api/v1/auth/logout");
  document.cookie.split(";").forEach(function (c) {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, `=;expires=${  new Date().toUTCString()  };path=/`);
  });
  if (response.status !== 200) {
    throw new Error(`Logout failed with status: ${response.status}`);
  }
};

export const registerAccount = async (data: accountInfo) => {
  if (!process.env.REACT_APP_BASE_URL) {
    return { data: null, error: "Configuration error: Base URL is missing." };
  }
  try {
    const response = await axiosInstance.post("/api/v1/auth/register", data);
    return { data: response.data, error: null };
  } catch (error) {
    return handleAxiosError(error as AxiosError);
  }
};

export const checkAuthFunction = async () => {
  try {
    const response = await axiosInstance.get("/api/v1/auth/check");
    if (response.status !== 200) {
      throw new Error(`Auth check failed with status: ${response.status}`);
    }

    const data = response.data;

    // Check if session expired due to inactivity
    // /api/v1/auth/check also returns sessionExpired: true when checking auth status
    if (data && typeof data === "object" && data.sessionExpired === true) {
      // Don't redirect if we're already on login or auth-related pages
      const currentPath = window.location.pathname;
      const isOnLoginPage = currentPath === "/login" || currentPath.startsWith("/login");
      const isOnAuthPage = currentPath === "/2fa-verify" || currentPath.startsWith("/2fa-verify");
      const isOnPublicAuthPage =
        currentPath.startsWith("/forgot-password") ||
        currentPath.startsWith("/reset-password") ||
        currentPath.startsWith("/set-password");

      // Clear any session storage data; keep localStorage (user preferences) intact
      sessionStorage.clear();

      if (isOnLoginPage || isOnAuthPage || isOnPublicAuthPage) {
        // Already on login page, just log and throw error (don't redirect)
        logger.warn("Session expired during auth check (already on login page)", {
          message: data.message || "Session expired",
          currentPath,
          context: "checkAuthFunction",
        });
        throw new Error("Session expired");
      }

      // Log session expiration
      logger.warn("Session expired during auth check", {
        message: data.message || "Session expired due to inactivity. Please login again.",
        redirectTo: data.redirectTo || "/login",
        context: "checkAuthFunction",
      });

      // Redirect to login
      window.location.href = data.redirectTo || "/login";
      throw new Error("Session expired");
    }

    return data;
  } catch (error) {
    // If it's a session expiration error, don't re-throw as it's already handled
    if (error instanceof Error && error.message === "Session expired") {
      throw error;
    }
    throw error;
  }
};

export const initiateMicrosoftLogin = () => {
  // Redirect to your backend OIDC endpoint
  // This should match the route you've configured in your Passport OIDC setup
  if (!process.env.REACT_APP_BASE_URL) {
    logger.error("REACT_APP_BASE_URL is not defined", {
      context: "initiateMicrosoftLogin",
    });
    return;
  }

  const oidcUrl = `${process.env.REACT_APP_BASE_URL}/api/v1/auth/microsoft/login`;

  // Ensure we're doing a proper redirect, not a POST request
  window.location.replace(oidcUrl);
};

export const handleOIDCCallback = async (code: string, state: string) => {
  try {
    const { data } = await axiosInstance.get(
      `/api/v1/auth/microsoft/callback?code=${code}&state=${state}`
    );

    const result = {
      success: data?.success ?? false,
      message:
        data?.message ||
        (data?.success ? "Microsoft authentication successful" : "Authentication failed"),
    };

    return result;
  } catch (error: any) {
    logger.error("OIDC callback error occurred", {
      error,
      errorType: error.constructor.name,
      errorMessage: error.message,
      status: error.response?.status,
      responseData: error.response?.data,
      responseHeaders: error.response?.headers,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      requestBaseURL: error.config?.baseURL,
      context: "handleOIDCCallback",
    });

    if (error instanceof AxiosError) {
      logger.error("OIDC callback error occurred", {
        error,
        context: "handleOIDCCallback",
      });
      const errorData = error.response?.data as { message?: string } | undefined;
      const errorResult = {
        success: false,
        message: errorData?.message || "Authentication callback failed",
      };

      return errorResult;
    }

    // Handle non-Axios errors
    if (error instanceof Error) {
      return {
        success: false,
        message: error.message,
      };
    }

    return {
      success: false,
      message: "Authentication callback failed",
    };
  }
};

// Accept Privacy & Terms for current user
export interface AcceptPrivacyResponse {
  message: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    authProvider: string | null;
    acceptPrivacy: boolean;
  };
}

export const postAcceptPrivacy = async (): Promise<AcceptPrivacyResponse> => {
  try {
    const response = await axiosInstance.post("/api/v1/auth/accept-privacy");
    const data = response?.data as AcceptPrivacyResponse;
    return data;
  } catch (error) {
    let message = "Failed to update privacy acceptance";

    if (error instanceof AxiosError) {
      const errorData = error.response?.data as { error?: string } | undefined;
      message = errorData?.error || message;
    } else if (error instanceof Error) {
      message = error.message;
    }

    throw new Error(message);
  }
};

function handleAxiosError(error: AxiosError): { data: null; error: string } {
  let errorMessage = "An unknown error occurred.";
  if (error.response) {
    const errorData = error.response.data as errorResponse;
    errorMessage = errorData.message || "An error occurred during registration.";
  } else if (error.request) {
    errorMessage = "Network error. Please check your connection.";
  } else {
    errorMessage = error.message;
  }
  return { data: null, error: errorMessage };
}

export const forgotPassword = async (email: string): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.post("/api/v1/auth/forgot-password", { email });
    return response.data as { message: string };
  } catch (error) {
    logger.error("Forgot password error", {
      error,
      context: "forgotPassword",
      endpoint: "/api/v1/auth/forgot-password",
    });
    if (error instanceof AxiosError) {
      const errorData = error.response?.data as { error?: string; message?: string } | undefined;
      throw new Error(errorData?.error || errorData?.message || "Failed to send reset email");
    }
    throw error;
  }
};

export const verifyResetToken = async (
  token: string
): Promise<{ valid: boolean; email?: string; error?: string }> => {
  try {
    const response = await axiosInstance.post("/api/v1/auth/verify-reset-token", { token });
    return response.data as { valid: boolean; email?: string };
  } catch (error) {
    logger.error("Verify reset token error", {
      error,
      context: "verifyResetToken",
      endpoint: "/api/v1/auth/verify-reset-token",
    });
    if (error instanceof AxiosError) {
      const errorData = error.response?.data as { valid?: boolean; error?: string } | undefined;
      return { valid: false, error: errorData?.error || "Invalid or expired token" };
    }
    return { valid: false, error: "An error occurred while verifying the token" };
  }
};

export const resetPasswordRequest = async (
  token: string,
  password: string
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.post("/api/v1/auth/reset-password", { token, password });
    return response.data as { message: string };
  } catch (error) {
    logger.error("Reset password error", {
      error,
      context: "resetPasswordRequest",
      endpoint: "/api/v1/auth/reset-password",
    });
    if (error instanceof AxiosError) {
      const errorData = error.response?.data as { error?: string; message?: string } | undefined;
      throw new Error(errorData?.error || errorData?.message || "Failed to reset password");
    }
    throw error;
  }
};

export const verifyInviteToken = async (
  token: string
): Promise<{ valid: boolean; email?: string; name?: string; error?: string }> => {
  try {
    const response = await axiosInstance.post("/api/v1/auth/verify-invite-token", { token });
    return response.data as { valid: boolean; email?: string; name?: string };
  } catch (error) {
    logger.error("Verify invite token error", {
      error,
      context: "verifyInviteToken",
      endpoint: "/api/v1/auth/verify-invite-token",
    });
    if (error instanceof AxiosError) {
      const errorData = error.response?.data as { valid?: boolean; error?: string } | undefined;
      return { valid: false, error: errorData?.error || "Invalid or expired invite" };
    }
    return { valid: false, error: "An error occurred while verifying the invitation" };
  }
};

export const acceptInvite = async (
  token: string,
  password: string
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.post("/api/v1/auth/accept-invite", { token, password });
    return response.data as { message: string };
  } catch (error) {
    logger.error("Accept invite error", {
      error,
      context: "acceptInvite",
      endpoint: "/api/v1/auth/accept-invite",
    });
    if (error instanceof AxiosError) {
      const errorData = error.response?.data as { error?: string; message?: string } | undefined;
      throw new Error(errorData?.error || errorData?.message || "Failed to accept invitation");
    }
    throw error;
  }
};

export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.post("/api/v1/auth/change-password", {
      currentPassword,
      newPassword,
    });
    return response.data as { message: string };
  } catch (error) {
    logger.error("Change password error", {
      error,
      context: "changePassword",
      endpoint: "/api/v1/auth/change-password",
    });
    if (error instanceof AxiosError) {
      if (error.response?.status === 429) {
        throw new Error("Too many attempts. Please try again later.");
      }
      const errorData = error.response?.data as { error?: string; message?: string } | undefined;
      throw new Error(errorData?.error || errorData?.message || "Failed to change password");
    }
    throw error;
  }
};
